import {useEffect, useState} from 'react';
import {FriendReferral} from 'casa-ui';
import {getConfig, sendLinkV2, sendOtp, shortenURL, verifyOtp} from "../../service/referFriend.service";
import _ from "lodash";
import {Box} from "@mui/system";
import {useLocation, useParams} from "react-router-dom";
import routesPath from "../../routes/routesPath";
import ROUTE_PATHS from "../../routes/routesPath";
import {validateCustomer} from "../../service/customerVerification";
import jwt_decode from "jwt-decode";
import {toast} from "react-hot-toast";
import useAuth from "../../hooks/useAuth";

const ReferFriendV3 = () => {
    const data = useAuth()
    const mobile = _.get(data, 'user.mobile', '');
    const business = _.get(data, 'user.business', _.get(data, 'user.businessUnit', ''));
    const businessUnitId = _.parseInt(_.get(data, 'user.businessUnitId', '0'));
    const userToken = _.get(data, 'token', '');

    const [ config, setConfig ] = useState();
    const [ tenantInfo, setTenantInfo ] = useState({});
    const [ shortenedShareableLink, setShortenedShareableLink ] = useState('');

    const getConfigToken = () => _.get(config, 'token');

    const getDataFromToken = (token) => {
        if (token !== null) {
            try {
                return jwt_decode(token)
            } catch(err) {
                console.log(err)
                return {}
            }
        }
        return {}
    };

    const location = useLocation();
    const currentPath = _.trimEnd(location.pathname, '/');

    const {businessHashId} = useParams()

    const getScreen = () => {
        if (currentPath === routesPath.REFER_FRIEND || currentPath === routesPath.REFER_FRIEND_PWA) {
            return 'directInvite'
        }
        if (currentPath === routesPath.FRIEND_ENROL) {
            return 'shareableLink'
        }
        return 'login'
    }

    const screen = getScreen()

    useEffect(() => {
        let isNotDirect = screen !== 'login' || !businessHashId;
        let param = isNotDirect ? 'name' : 'hashId';
        let value = isNotDirect ? business : businessHashId;
        if (_.isEmpty(value)) {
            param = 'id'
            value = businessUnitId;
        }
        getConfig(param, value)
            .then((cfg) => {
                setConfig(cfg)
                const token = _.get(cfg, 'token', null)
                setTenantInfo(getDataFromToken(token))
            })
            .catch((err) => {
                console.log(err)
                setConfig(null)
            })
    }, [business, screen, businessHashId, businessUnitId]);


    useEffect(() => {
        const shareableLink =  `${window.location.origin}${ROUTE_PATHS.FRIEND_ENROL}?data=${userToken}`
        if (screen === 'directInvite') {
            shortenURL(userToken, shareableLink)
                .then(url => setShortenedShareableLink(url))
                .catch(err => {
                    console.log(err)
                    setShortenedShareableLink(shareableLink)
                })
        }
    }, [screen, userToken])

    const handleReferralResponse = (response) => {
        const {
            validReferrals = [],
            alreadyReferredUsers = [],
            existingUsers = [],
            invalidUsers = []
        } = response;

        if (validReferrals.length > 0) {
            const message = screen === 'directInvite' ? 'sent invite' : 'enrolled';
            toast.success(`Successfully ${message}.`);
            return;
        }

        const representUser = (name, mobile) => {
            if (name) {
                return `${name} - ${mobile}`
            }
            return mobile
        }

        const errorCases = [
            { data: alreadyReferredUsers, message: (name, mobile) => `"${representUser(name, mobile)}" has already been referred.` },
            { data: existingUsers, message: (name, mobile) => `"${representUser(name, mobile)}" is already an existing user.` },
            { data: invalidUsers, message: (name, mobile) => `Invalid details for "${representUser(name, mobile)}". Please check again.` }
        ];

        for (const { data, message } of errorCases) {
            if (data.length > 0) {
                toast.error(message(data[0].name, data[0].mobile));
                return;
            }
        }

        toast.error("Apologies. Something went wrong. Invite could not be sent.");
    };


    const onSubmit = async (data) => {
        const referrals = _.isArray(data) ? data : [data];
        const referralPayload = {
            referrals,
            screen
        };

        try {
            const response = await sendLinkV2(referralPayload);
            handleReferralResponse(response);
        } catch (err) {
            console.error(err);
            toast.error("Could not complete the request. Something went wrong.");
        }
    };

    const onSendOtp = async (mobile) => {
        const customerTypeInfo = await validateCustomer(mobile, tenantInfo.tenant, tenantInfo.business);
        if (customerTypeInfo.status !== 'Existing') {
            const message = "You are not allowed to refer.";
            toast.error(message)
            throw message
        }
        await sendOtp(mobile, {token: getConfigToken()}, 'referral-otp')
            .then(() => {
                toast.success(`OTP sent to ${mobile}`)
            })
            .catch((err) => {
                console.log(err)
                const message = `Apologies. OTP could not be sent.`;
                toast.error(message)
            });
    }

    const onVerifyOtp = async (mobile) => {
        const result = await verifyOtp(
            mobile.mobile,
            { token: getConfigToken() },
            mobile.otp,
            'referral-otp'
        ).catch((err) => {
            console.log("🚀 ~ onVerifyOtp ~ err: ", err);
            toast.error(`Invalid OTP.`)
        });

        if (result.status === 'success') {
            const customerTypeInfo = await validateCustomer(mobile.mobile, tenantInfo.tenant, tenantInfo.business);
            redirectToInviteScreen(customerTypeInfo.redirectUrl || '')
            return
        }

        toast.error(`Invalid OTP.`)
    }

    const redirectToInviteScreen = (url) => {
        const urlObj = new URL(url);
        const params = new URLSearchParams(urlObj.search)
        const userToken = params.get('data');
        const link = `${window.location.origin}${ROUTE_PATHS.REFER_FRIEND}?data=${userToken}`
        window.location.href = link
    }

    return !!config && (
        <Box px={1} pb={1}>
            <FriendReferral
                config={_.get(config, 'config')}
                onSubmit={onSubmit}
                screen={screen}
                user={mobile}
                previewMode={false}
                shareableLink={shortenedShareableLink}
                onOTPSend={onSendOtp}
                onOtpVerify={onVerifyOtp}
            />
        </Box>
    )
}

export default ReferFriendV3;
